import styled, { keyframes } from 'styled-components';

/* ===== Page layout ===== */

export const StyledTopBar = styled.div`
  display: flex;
  align-items: center;
  gap: var(--s-3);
  margin-bottom: var(--s-4);
`;

export const StyledSearchForm = styled.form`
  flex: 1;
  max-width: 28rem;
`;

export const StyledCartButton = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: var(--r-md);
  border: 1px solid var(--line-2);
  background: var(--surface);
  color: var(--ink);
  cursor: pointer;
  transition: all var(--dur-fast) var(--ease-out);
  flex-shrink: 0;

  svg {
    width: 1.125rem;
    height: 1.125rem;
  }

  &:hover {
    border-color: var(--brand-green);
    color: var(--brand-green);
    background: var(--brand-green-soft);
    transform: translateY(-1px);
  }
  &:active {
    transform: translateY(0);
  }
`;

export const StyledCartBadge = styled.span`
  position: absolute;
  top: -0.25rem;
  right: -0.25rem;
  background: var(--brand-green);
  color: var(--surface);
  font-size: 0.65rem;
  font-weight: 700;
  min-width: 1.1rem;
  height: 1.1rem;
  border-radius: 999px;
  padding: 0 0.3rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const StyledCategoryBar = styled.div`
  display: flex;
  gap: var(--s-2);
  overflow-x: auto;
  padding: var(--s-1) 0;
  margin-bottom: var(--s-4);
  -webkit-overflow-scrolling: touch;
  flex-shrink: 0;

  &::-webkit-scrollbar {
    display: none;
  }
`;

/* === Brands row === */

export const StyledBrandsSection = styled.section`
  margin-bottom: var(--s-4);
`;

export const StyledBrandsHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: var(--s-2);
`;

export const StyledBrandsTitle = styled.h2`
  font-size: var(--t-xs);
  font-weight: var(--weight-semibold);
  text-transform: uppercase;
  letter-spacing: var(--tracking-eyebrow);
  color: var(--muted);
  margin: 0;
`;

export const StyledBrandsClear = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: var(--t-xs);
  font-weight: var(--weight-medium);
  color: var(--brand-green);
  padding: 0;
  transition: opacity var(--dur-fast) var(--ease-out);
  &:hover {
    opacity: 0.75;
  }
`;

export const StyledBrandsScroll = styled.div`
  display: flex;
  gap: var(--s-2);
  overflow-x: auto;
  padding: var(--s-2) 0;
  margin-bottom: var(--s-4);
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 3px;
  }
  &::-webkit-scrollbar-track {
    background: var(--line);
    border-radius: var(--r-full);
  }
  &::-webkit-scrollbar-thumb {
    background: var(--line-2);
    border-radius: var(--r-full);
  }
`;

export const StyledBrandTile = styled.button<{ active?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--s-1);
  padding: var(--s-3) var(--s-3);
  border-radius: var(--r-md);
  border: 1.5px solid ${({ active }) => active ? 'var(--brand-green)' : 'var(--line)'};
  background: ${({ active }) => active ? 'var(--brand-green-soft)' : 'var(--paper)'};
  cursor: pointer;
  flex-shrink: 0;
  transition: all var(--dur-fast) var(--ease-out);
  min-width: 6rem;

  &:hover {
    border-color: var(--brand-green);
    box-shadow: var(--sh-1);
  }
`;

/* StyledBrandLogo kept for backward-compat but hidden in new layout */
export const StyledBrandLogo = styled.div<{ active?: boolean }>`
  display: none;
`;

export const StyledBrandName = styled.span<{ active?: boolean }>`
  font-size: var(--t-sm);
  font-weight: 700;
  color: ${({ active }) => active ? 'var(--brand-green)' : 'var(--ink)'};
  white-space: nowrap;
`;

export const StyledBrandCount = styled.span`
  font-size: var(--t-xs);
  color: var(--muted);
  font-variant-numeric: tabular-nums;
`;

export const StyledCategoryChip = styled.button<{ active?: boolean }>`
  padding: 0.375rem 0.875rem;
  border-radius: var(--r-full);
  border: 1px solid ${({ active }) => active ? 'transparent' : 'var(--line)'};
  background: ${({ active }) => active ? 'var(--brand-ink)' : 'var(--paper)'};
  color: ${({ active }) => active ? 'var(--surface)' : 'var(--ink)'};
  font-size: var(--t-sm);
  font-weight: ${({ active }) => (active ? 600 : 500)};
  white-space: nowrap;
  flex-shrink: 0;
  cursor: pointer;
  transition: all var(--dur-fast) var(--ease-out);

  &:hover {
    border-color: var(--brand-ink);
    background: ${({ active }) => active ? 'var(--brand-ink-2)' : 'var(--paper-2)'};
  }
`;

/* ===== Card grid ===== */

export const StyledGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--s-4);

  @media (max-width: 1280px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

export const StyledCard = styled.div`
  background: var(--surface);
  border-radius: var(--r-lg);
  overflow: hidden;
  border: 1px solid var(--line);
  box-shadow: var(--sh-1);
  display: flex;
  flex-direction: column;
  transition: box-shadow var(--dur-base) var(--ease-out),
    border-color var(--dur-base) var(--ease-out);

  &:hover {
    box-shadow: var(--sh-2);
    border-color: var(--line-2);
  }
`;

export const StyledCardImageWrap = styled.div`
  position: relative;
  aspect-ratio: 5 / 4;
  background: linear-gradient(135deg, var(--paper) 0%, var(--paper-2) 100%);
  display: grid;
  place-items: center;
  overflow: hidden;
`;

export const StyledCardEmoji = styled.span`
  font-size: 3.5rem;
  line-height: 1;
  display: block;
  transition: transform 320ms cubic-bezier(0.4, 0, 0.2, 1);

  ${StyledCard}:hover & {
    transform: scale(1.1);
  }
`;

export const StyledCardBrandBadge = styled.span`
  position: absolute;
  top: 10px;
  left: 10px;
  background: var(--brand-ink);
  color: var(--surface);
  border-radius: var(--r-full);
  font-size: var(--t-xs);
  font-weight: 600;
  padding: 3px var(--s-2);
  letter-spacing: 0.02em;
  line-height: 1.4;
`;

/* Legacy - kept for ProductDetailModal backward-compat */
export const StyledCardImage = styled.div<{ bg?: string }>`
  height: 8rem;
  background: var(--paper);
  border-bottom: 1px solid var(--line);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  overflow: hidden;

  > * {
    transition: transform 320ms cubic-bezier(0.4, 0, 0.2, 1);
    display: inline-block;
  }
`;

export const StyledCardBody = styled.div`
  padding: var(--s-4);
  display: flex;
  flex-direction: column;
  flex: 1;
`;

export const StyledCardCategory = styled.span`
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: var(--muted);
  margin-bottom: var(--s-1);
  display: block;
`;

export const StyledCardTitle = styled.h3`
  font-size: var(--t-base);
  font-weight: 700;
  color: var(--ink);
  margin: 0 0 var(--s-1);
  line-height: 1.3;
  letter-spacing: -0.005em;
`;

export const StyledCardDescription = styled.p`
  font-size: var(--t-sm);
  color: var(--muted);
  margin: 0 0 var(--s-3);
  line-height: 1.4;
  flex: 1;
`;

export const StyledCardFooter = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: var(--s-3);
`;

export const StyledPriceBlock = styled.div`
  flex: 1;
`;

export const StyledPrice = styled.div`
  font-size: var(--t-lg);
  font-weight: 700;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.02em;
  display: inline-flex;
  align-items: baseline;
  gap: var(--s-1);
  line-height: 1;

  small {
    font-size: var(--t-xs);
    font-weight: 500;
    color: var(--muted);
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
`;

export const StyledMinQty = styled.div`
  font-size: var(--t-xs);
  color: var(--muted);
  font-variant-numeric: tabular-nums;
  text-align: right;
  flex-shrink: 0;
`;

export const StyledBuyButton = styled.button`
  width: 100%;
  padding: var(--s-2) var(--s-3);
  border-radius: var(--r-md);
  background: var(--brand-green);
  color: var(--surface);
  font-size: var(--t-sm);
  font-weight: var(--weight-semibold);
  border: none;
  cursor: pointer;
  transition: filter var(--dur-fast) var(--ease-out),
    transform var(--dur-fast) var(--ease-out),
    box-shadow var(--dur-fast) var(--ease-out);

  &:hover {
    filter: brightness(0.93);
    transform: translateY(-1px);
    box-shadow: var(--sh-1);
  }
  &:active {
    transform: translateY(0);
    filter: brightness(0.88);
  }
`;

export const StyledEmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 0;
  color: var(--muted);
  font-size: 0.9375rem;
`;

/* ===== Overlay shared ===== */

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(1rem); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideRight = keyframes`
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
`;

export const StyledOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(9, 15, 56, 0.45);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  animation: ${fadeIn} 0.18s ease;
`;

/* ===== Product detail modal ===== */

export const StyledModalCard = styled.div`
  background: var(--surface);
  border-radius: var(--r-xl);
  width: 100%;
  max-width: 52rem;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: var(--sh-4);
  animation: ${slideUp} 0.22s ease;
  display: flex;
  flex-direction: column;
`;

export const StyledModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--line);
`;

export const StyledModalTitle = styled.h2`
  font-size: var(--t-lg);
  font-weight: var(--weight-bold);
  color: var(--ink);
  margin: 0;
  letter-spacing: var(--tracking-tight);
`;

export const StyledCloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  width: 2rem;
  height: 2rem;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--muted);
  transition: background var(--dur-fast) var(--ease-out);

  svg {
    width: 1rem;
    height: 1rem;
  }

  &:hover {
    background: var(--paper);
    color: var(--ink);
  }
`;

export const StyledProductDetailBody = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  padding: 1.5rem;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

export const StyledProductImageLarge = styled.div<{ bg: string }>`
  background: ${({ bg }) => bg};
  border-radius: var(--r-lg);
  height: 18rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 6rem;
`;

export const StyledProductInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

export const StyledFullDescription = styled.p`
  font-size: var(--t-sm);
  color: var(--muted);
  line-height: 1.55;
  margin: 0.5rem 0 1rem;
`;

export const StyledSpecsList = styled.dl`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.5rem 1rem;
  margin: 0 0 1.25rem;
  font-size: 0.8125rem;

  dt {
    color: var(--muted);
    font-weight: var(--weight-medium);
  }

  dd {
    color: var(--ink);
    margin: 0;
    font-weight: var(--weight-medium);
  }
`;

export const StyledCertBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
`;

export const StyledCertBadge = styled.span`
  font-size: 0.7rem;
  font-weight: var(--weight-semibold);
  padding: 0.2rem 0.5rem;
  background: var(--paper);
  color: var(--muted);
  border-radius: var(--r-xs);
  border: 1px solid var(--line);
`;

export const StyledQtyRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--paper);
  border-radius: var(--r-sm);
  padding: 0.875rem 1rem;
  margin-top: 1rem;
`;

export const StyledQtyLabel = styled.div`
  font-size: 0.8125rem;
  color: var(--muted);

  strong {
    display: block;
    font-size: 0.9rem;
    color: var(--ink);
    margin-bottom: 0.125rem;
    font-weight: var(--weight-semibold);
  }
`;

export const StyledQtyStepper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const StyledStepperButton = styled.button`
  width: 2rem;
  height: 2rem;
  border-radius: var(--r-sm);
  border: 1.5px solid var(--line);
  background: var(--surface);
  font-size: 1rem;
  font-weight: var(--weight-bold);
  color: var(--ink);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all var(--dur-fast) var(--ease-out);

  &:hover:not(:disabled) {
    border-color: var(--brand-green);
    color: var(--brand-green);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const StyledQtyInput = styled.input`
  width: 4rem;
  text-align: center;
  font-size: 0.9375rem;
  font-weight: var(--weight-semibold);
  border: 1.5px solid var(--line);
  border-radius: var(--r-sm);
  padding: 0.375rem;
  background: var(--surface);
  color: var(--ink);

  &:focus {
    outline: none;
    border-color: var(--brand-green);
  }
`;

export const StyledTotalRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--line);
  background: var(--paper);
`;

export const StyledTotalLabel = styled.div`
  font-size: 0.8125rem;
  color: var(--muted);
`;

export const StyledTotalValue = styled.div`
  font-size: var(--t-2xl);
  font-weight: var(--weight-bold);
  color: var(--ink);
  font-variant-numeric: tabular-nums;
`;

export const StyledModalFooter = styled.div`
  padding: 1rem 1.5rem;
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  border-top: 1px solid var(--line);
`;

export const StyledSecondaryButton = styled.button`
  padding: 0.625rem 1.25rem;
  border-radius: var(--r-sm);
  background: var(--surface);
  border: 1.5px solid var(--line);
  color: var(--ink);
  font-size: var(--t-sm);
  font-weight: var(--weight-semibold);
  cursor: pointer;
  transition: all var(--dur-fast) var(--ease-out);

  &:hover {
    border-color: var(--brand-green);
    color: var(--brand-green);
  }
`;

export const StyledPrimaryButton = styled.button`
  padding: 0.625rem 1.5rem;
  border-radius: var(--r-sm);
  background: var(--brand-green);
  color: var(--surface);
  border: none;
  font-size: var(--t-sm);
  font-weight: var(--weight-semibold);
  cursor: pointer;
  transition: opacity var(--dur-fast) var(--ease-out);

  &:hover {
    opacity: 0.88;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/* ===== Cart drawer ===== */

export const StyledDrawerOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(9, 15, 56, 0.45);
  z-index: 1000;
  animation: ${fadeIn} 0.18s ease;
`;

export const StyledDrawer = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  max-width: 25rem;
  background: var(--surface);
  z-index: 1001;
  display: flex;
  flex-direction: column;
  animation: ${slideRight} 0.22s ease;
  box-shadow: var(--sh-4);
`;

export const StyledDrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--line);
`;

export const StyledDrawerBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem 1.5rem;
`;

export const StyledDrawerFooter = styled.div`
  border-top: 1px solid var(--line);
  padding: 1.25rem 1.5rem;
  background: var(--paper);
`;

export const StyledCartItem = styled.div`
  display: grid;
  grid-template-columns: 3rem 1fr auto;
  gap: 0.875rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--line);
  align-items: center;

  &:last-child {
    border-bottom: none;
  }
`;

export const StyledCartItemImage = styled.div<{ bg: string }>`
  width: 3rem;
  height: 3rem;
  background: ${({ bg }) => bg};
  border-radius: var(--r-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
`;

export const StyledCartItemInfo = styled.div`
  min-width: 0;
`;

export const StyledCartItemName = styled.div`
  font-size: 0.8125rem;
  font-weight: var(--weight-semibold);
  color: var(--ink);
  margin-bottom: 0.125rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const StyledCartItemMeta = styled.div`
  font-size: 0.72rem;
  color: var(--muted);
`;

export const StyledCartItemPrice = styled.div`
  font-size: var(--t-sm);
  font-weight: var(--weight-bold);
  color: var(--ink);
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`;

export const StyledRemoveButton = styled.button`
  background: none;
  border: none;
  color: var(--muted);
  font-size: 0.7rem;
  cursor: pointer;
  text-decoration: underline;
  margin-top: 0.25rem;
  padding: 0;
  text-align: right;
  display: block;
  margin-left: auto;

  &:hover {
    color: var(--danger);
  }
`;

export const StyledCartTotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 1rem;
`;

export const StyledCartTotalLabel = styled.div`
  font-size: var(--t-sm);
  color: var(--muted);
`;

export const StyledCartTotalValue = styled.div`
  font-size: var(--t-2xl);
  font-weight: var(--weight-bold);
  color: var(--ink);
  font-variant-numeric: tabular-nums;
`;

export const StyledCheckoutButton = styled.button`
  width: 100%;
  padding: 0.875rem;
  border-radius: var(--r-sm);
  background: var(--brand-green);
  color: var(--surface);
  border: none;
  font-size: var(--t-base);
  font-weight: var(--weight-bold);
  cursor: pointer;
  transition: opacity var(--dur-fast) var(--ease-out);

  &:hover:not(:disabled) {
    opacity: 0.88;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const StyledEmptyCart = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: var(--muted);
  font-size: var(--t-sm);

  div:first-child {
    font-size: 3rem;
    margin-bottom: 0.75rem;
  }
`;

/* ===== Checkout ===== */

export const StyledCheckoutBody = styled.div`
  display: grid;
  grid-template-columns: 1fr 22rem;
  gap: 1.5rem;
  padding: 1.5rem;

  @media (max-width: 880px) {
    grid-template-columns: 1fr;
  }
`;

export const StyledCheckoutSection = styled.section`
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const StyledCheckoutSectionTitle = styled.h3`
  font-size: var(--t-xs);
  font-weight: var(--weight-bold);
  text-transform: uppercase;
  letter-spacing: var(--tracking-eyebrow);
  color: var(--muted);
  margin: 0 0 0.75rem;
`;

export const StyledFormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const StyledFormGroup = styled.div<{ fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  ${({ fullWidth }) => fullWidth && 'grid-column: 1 / -1;'}
`;

export const StyledLabel = styled.label`
  font-size: 0.75rem;
  font-weight: var(--weight-semibold);
  color: var(--muted);
  margin-bottom: 0.375rem;
`;

export const StyledFormInput = styled.input`
  padding: 0.625rem 0.75rem;
  border: 1.5px solid var(--line);
  border-radius: var(--r-sm);
  background: var(--surface);
  font-size: var(--t-sm);
  color: var(--ink);
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: var(--brand-green);
  }
`;

export const StyledTextarea = styled.textarea`
  padding: 0.625rem 0.75rem;
  border: 1.5px solid var(--line);
  border-radius: var(--r-sm);
  background: var(--surface);
  font-size: var(--t-sm);
  color: var(--ink);
  font-family: inherit;
  min-height: 4rem;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: var(--brand-green);
  }
`;

export const StyledPaymentOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const StyledPaymentOption = styled.label<{ selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  border: 1.5px solid ${({ selected }) => selected ? 'var(--brand-green)' : 'var(--line)'};
  border-radius: var(--r-sm);
  cursor: pointer;
  background: ${({ selected }) => selected ? 'var(--brand-green-soft)' : 'var(--surface)'};
  font-size: var(--t-sm);
  color: var(--ink);
  font-weight: var(--weight-medium);
  transition: all var(--dur-fast) var(--ease-out);

  input {
    accent-color: var(--brand-green);
  }

  span {
    font-size: 1.125rem;
    margin-right: 0.25rem;
  }
`;

export const StyledOrderSummary = styled.aside`
  background: var(--paper);
  border-radius: var(--r-lg);
  padding: 1.25rem;
  align-self: start;
  position: sticky;
  top: 0;
  border: 1px solid var(--line);
`;

export const StyledSummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0.5rem 0;
  font-size: 0.8125rem;
  color: var(--ink);
  border-bottom: 1px solid var(--line);

  &:last-of-type {
    border-bottom: none;
  }
`;

export const StyledSummaryItemInfo = styled.div`
  flex: 1;
  padding-right: 0.5rem;

  strong {
    display: block;
    font-size: 0.8125rem;
    font-weight: var(--weight-semibold);
    margin-bottom: 0.125rem;
  }
  span {
    font-size: 0.72rem;
    color: var(--muted);
  }
`;

export const StyledSummaryRow = styled.div<{ bold?: boolean }>`
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  font-size: ${({ bold }) => (bold ? 'var(--t-base)' : '0.8125rem')};
  font-weight: ${({ bold }) => (bold ? 700 : 500)};
  color: var(--ink);
  border-top: ${({ bold }) => bold ? '1px solid var(--line)' : 'none'};
  margin-top: ${({ bold }) => (bold ? '0.5rem' : 0)};
  padding-top: ${({ bold }) => (bold ? '0.75rem' : '0.5rem')};
  font-variant-numeric: tabular-nums;
`;

/* ===== Success ===== */

export const StyledSuccessBox = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const StyledSuccessIcon = styled.div`
  width: 4.5rem;
  height: 4.5rem;
  border-radius: 999px;
  background: var(--brand-green);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--surface);
  font-size: 2rem;
  margin-bottom: 1.25rem;
`;

export const StyledSuccessTitle = styled.h2`
  font-size: var(--t-2xl);
  font-weight: var(--weight-bold);
  color: var(--ink);
  margin: 0 0 0.5rem;
  letter-spacing: var(--tracking-tight);
`;

export const StyledSuccessMessage = styled.p`
  font-size: var(--t-base);
  color: var(--muted);
  margin: 0 0 1.5rem;
  max-width: 28rem;
`;

export const StyledOrderRef = styled.div`
  font-size: var(--t-sm);
  font-weight: var(--weight-semibold);
  color: var(--ink);
  background: var(--paper);
  border: 1px solid var(--line);
  padding: 0.75rem 1.25rem;
  border-radius: var(--r-sm);
  margin-bottom: 1.5rem;

  span {
    color: var(--brand-green-ink);
  }
`;
